/* We use many `any` on purpose in this module, so we deactivate the following rule : */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list'
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  FlatListProps,
  Platform,
  ScrollView,
  StyleProp,
  useWindowDimensions,
  ViewabilityConfig,
  ViewStyle,
} from 'react-native'
import { FlatList, FlatList as RNGHFlatList } from 'react-native-gesture-handler'
import styled, { useTheme } from 'styled-components/native'

import { PlaylistType } from 'features/offer/enums'
import { AbsoluteRoundedButton } from 'ui/components/buttons/AbsoluteRoundedButton'
import { Li } from 'ui/components/Li'
import { Ul } from 'ui/components/Ul'
import { useHorizontalFlatListScroll } from 'ui/hooks/useHorizontalFlatListScroll'

type ItemDimensions = { width: number; height: number }

type RenderHeaderItem = ((itemDimensions: ItemDimensions) => React.ReactElement<any>) | undefined

type RenderFooterItem =
  ((itemDimensions: ItemDimensions) => React.ReactElement<any> | null) | undefined

export type CustomListRenderItem<ItemT> = (
  info: ListRenderItemInfo<ItemT> &
    ItemDimensions & {
      playlistType?: PlaylistType
    }
) => React.ReactElement | null

type Props = Pick<FlatListProps<unknown>, 'onViewableItemsChanged'> & {
  data: any[]
  itemWidth: number
  itemHeight: number
  scrollButtonOffsetY?: number
  testID?: string
  renderItem: CustomListRenderItem<any>
  keyExtractor: (item: any, index: number) => string
  renderHeader?: RenderHeaderItem
  renderFooter?: RenderFooterItem
  onEndReached?: () => void
  children?: never
  tileType?: 'offer' | 'venue' | 'video-module-offer'
  playlistType?: PlaylistType
  FlatListComponent?: typeof FlashList | typeof RNGHFlatList
  itemSeparatorSize?: number
  horizontalMargin?: number
  contentContainerStyle?: StyleProp<ViewStyle>
  withMargins?: boolean
}

const isWeb = Platform.OS === 'web'

const PLAYLIST_VIEWABILITY_CONFIG = {
  waitForInteraction: true,
  viewAreaCoveragePercentThreshold: 20,
  minimumViewTime: 300,
} satisfies ViewabilityConfig

const InnerPlaylist = forwardRef<FlatList, Props>(function Playlist(props, ref) {
  const { designSystem } = useTheme()
  const {
    data,
    itemWidth,
    itemHeight,
    scrollButtonOffsetY,
    testID,
    playlistType,
    renderItem,
    keyExtractor,
    renderHeader,
    renderFooter,
    onEndReached,
    onViewableItemsChanged,
    FlatListComponent = FlashList,
    tileType = 'offer',
    itemSeparatorSize = designSystem.size.spacing.l,
    horizontalMargin = designSystem.size.spacing.xl,
    contentContainerStyle,
    withMargins = true,
  } = props

  const { isTouch, tiles } = useTheme()
  const { width } = useWindowDimensions()

  const listRef = useRef<any>(null)
  const webScrollRef = useRef<ScrollView>(null)
  const [webScrollPosition, setWebScrollPosition] = useState(0)
  const [webContainerWidth, setWebContainerWidth] = useState(0)

  const { onScroll, onContentSizeChange, onContainerLayout, isEnd, isStart } =
    useHorizontalFlatListScroll({
      ref: listRef,
      isActive: isWeb,
    })

  const handleWebScroll = useCallback(
    (event: any) => {
      onScroll(event)
      setWebScrollPosition(event.nativeEvent.contentOffset.x)
    },
    [onScroll]
  )

  const handleWebContainerLayout = useCallback(
    (event: any) => {
      onContainerLayout(event)
      setWebContainerWidth(event.nativeEvent.layout.width)
    },
    [onContainerLayout]
  )

  const handleWebScrollNext = useCallback(() => {
    webScrollRef.current?.scrollTo({ x: webScrollPosition + webContainerWidth, animated: true })
  }, [webScrollPosition, webContainerWidth])

  const handleWebScrollPrevious = useCallback(() => {
    webScrollRef.current?.scrollTo({
      x: Math.max(webScrollPosition - webContainerWidth, 0),
      animated: true,
    })
  }, [webScrollPosition, webContainerWidth])

  useImperativeHandle(ref, () => listRef.current as FlatList)

  // We have to include these dummy objects for header and footer in the data array
  // in order to have the correct array length available for the scroll functions and renderItem
  // See also renderItemWithHeaderAndFooter(...)
  const dataWithHeaderAndFooter = useMemo(() => {
    if (renderHeader && renderFooter) return [{ dataHeader: true }, ...data, { dataFooter: true }]
    if (renderHeader) return [{ dataHeader: true }, ...data]
    if (renderFooter) return [...data, { dataFooter: true }]
    return data
  }, [data, renderHeader, renderFooter])

  const nbOfItems = dataWithHeaderAndFooter.length

  const keyExtractorWithHeaderAndFooter = useCallback(
    function (item: any, index: number) {
      if (renderHeader && index === 0) return 'playlist-data-header'
      if (renderFooter && index === nbOfItems - 1) return 'playlist-data-footer'
      return keyExtractor(item, index)
    },
    [renderHeader, renderFooter, keyExtractor, nbOfItems]
  )

  const renderItemWithHeaderAndFooter = useCallback(
    function ({ item, index = -1 }: { item: object; index: number }) {
      if (renderHeader && index === 0) {
        return renderHeader({ height: itemHeight, width: itemWidth })
      }
      if (renderFooter && index === nbOfItems - 1) {
        return renderFooter({ height: itemHeight, width: itemWidth })
      }
      return renderItem({
        item,
        index,
        width: itemWidth,
        height: itemHeight,
        target: 'Cell',
        playlistType,
      })
    },
    [renderHeader, renderFooter, nbOfItems, renderItem, itemWidth, itemHeight, playlistType]
  )

  const MemoizedItemSeparatorComponent = useMemo(
    () => styled(ItemSeparatorComponent).attrs({ width: itemSeparatorSize })``,
    [itemSeparatorSize]
  )
  const MemoizedHorizontalMargin = useMemo(
    () => styled(HorizontalMargin).attrs({ width: horizontalMargin })``,
    [horizontalMargin]
  )

  const maxCaptionHeight =
    tileType === 'video-module-offer'
      ? tiles.maxCaptionHeight.videoModuleOffer
      : tiles.maxCaptionHeight[tileType]

  // To avoid a bug of cropped display on some home modules with Android, we need to add minHeigth to the FlashList Container
  const minHeight = Platform.OS === 'android' ? itemHeight + maxCaptionHeight : undefined

  const getItemLayout = useCallback(
    (data, index) => ({
      length: itemWidth,
      offset: (itemWidth + itemSeparatorSize) * index,
      index,
    }),
    [itemWidth, itemSeparatorSize]
  )

  return (
    <FlatListContainer onLayout={handleWebContainerLayout} minHeight={minHeight}>
      {!isStart && isWeb ? (
        <AbsoluteRoundedButton
          direction="left"
          top={scrollButtonOffsetY}
          iconName="previous"
          onPress={handleWebScrollPrevious}
          accessibilityLabel="Faire défiler la liste vers la gauche"
        />
      ) : null}
      {!isEnd && isWeb ? (
        <AbsoluteRoundedButton
          direction="right"
          top={scrollButtonOffsetY}
          iconName="next"
          onPress={handleWebScrollNext}
          accessibilityLabel="Faire défiler la liste vers la droite"
        />
      ) : null}

      {isWeb ? (
        <ScrollView
          ref={webScrollRef}
          testID={testID}
          onScroll={handleWebScroll}
          onContentSizeChange={onContentSizeChange}
          horizontal
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          style={contentContainerStyle}>
          <WebListContainer
            style={withMargins ? { paddingHorizontal: horizontalMargin } : undefined}>
            {dataWithHeaderAndFooter.map((item, index) => (
              <Li
                key={keyExtractorWithHeaderAndFooter(item, index)}
                style={index > 0 ? { marginLeft: itemSeparatorSize } : undefined}>
                {renderItemWithHeaderAndFooter({ item, index })}
              </Li>
            ))}
          </WebListContainer>
        </ScrollView>
      ) : (
        <FlatListComponent
          onScroll={onScroll}
          onContentSizeChange={onContentSizeChange}
          testID={testID}
          ref={listRef}
          scrollEnabled={isTouch}
          drawDistance={width / 4}
          data={dataWithHeaderAndFooter}
          renderItem={renderItemWithHeaderAndFooter}
          keyExtractor={keyExtractorWithHeaderAndFooter}
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={16}
          horizontal
          windowSize={7}
          initialNumToRender={4}
          maxToRenderPerBatch={6}
          removeClippedSubviews
          updateCellsBatchingPeriod={100}
          getItemLayout={getItemLayout}
          ItemSeparatorComponent={MemoizedItemSeparatorComponent}
          ListHeaderComponent={withMargins ? MemoizedHorizontalMargin : undefined}
          ListFooterComponent={MemoizedHorizontalMargin}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.2}
          viewabilityConfig={PLAYLIST_VIEWABILITY_CONFIG}
          onViewableItemsChanged={onViewableItemsChanged}
          contentContainerStyle={contentContainerStyle}
        />
      )}
    </FlatListContainer>
  )
})

export const Playlist = React.memo(InnerPlaylist)

const FlatListContainer = styled.View<{ minHeight?: number }>(({ minHeight }) => ({
  position: 'relative',
  width: '100%',
  minHeight,
}))

const WebListContainer = styled(Ul)({
  display: 'flex',
  flexDirection: 'row',
  overflowX: 'auto',
  width: '100%',
})

const HorizontalMargin = styled.View<{ width: number }>(({ width }) => ({
  width,
}))

const ItemSeparatorComponent = styled.View<{ width: number }>(({ width }) => ({ width }))
